import axios from 'axios';
import { UserData, Language } from '../config/types';
import MSALSingleton from './msalInstance';

interface SendAnalyticsParams {
  currentLanguage: Language;
}

interface SendAnalyticsResponse {
  content: string;
}

const getOrCreateUserId = () => {
  const existingUserId = localStorage.getItem('userId');
  if (!existingUserId) {
    const newUserId = `user_${crypto.randomUUID()}`;
    localStorage.setItem('userId', newUserId);
    return newUserId;
  }
  return existingUserId;
};

async function getAuthHeaders() {
  const instance = MSALSingleton.getInstance();
  const accounts = instance.getAllAccounts();

  if (accounts.length === 0) {
    return null;
  }

  try {
    const silentResult = await instance.acquireTokenSilent({
      scopes: ['User.Read'],
      account: accounts[0]
    });

    return {
      'X-Auth-Token': silentResult.accessToken,
      'X-Auth-Provider': 'microsoft'
    };
  } catch (error) {
    console.error('Failed to acquire token:', error);
    return null;
  }
}

const collectUserData = async (currentLanguage: Language): Promise<UserData> => {
  let userId = 'unknown_user';
  try {
    if (!MSALSingleton.isInitialized()) {
      await MSALSingleton.initialize();
    }
    const instance = MSALSingleton.getInstance();
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0) {
      userId = accounts[0].username;
    }
  } catch (error) {
    console.error('Error getting MSAL account:', error);
    userId = getOrCreateUserId();
  }

  // Get IP address first
  let userData: UserData = {
    userId,
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    screenResolution: {
      width: window.screen.width,
      height: window.screen.height,
      colorDepth: window.screen.colorDepth
    },
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    referrer: document.referrer,
    pathname: window.location.pathname,
    currentLanguage
  };

  try {
    const ipResponse = await axios.get('https://api.ipify.org?format=json');
    userData.ip = ipResponse.data.ip;

    // Only make ipstack request if we successfully got the IP
    const ipstackApiKey = process.env.REACT_APP_IPSTACK_API_KEY;
    if (ipstackApiKey && userData.ip) {
      const geoResponse = await axios.get(
        `https://api.ipstack.com/${userData.ip}?access_key=${ipstackApiKey}`
      );
      
      userData.location = {
        country: geoResponse.data.country_name,
        region: geoResponse.data.region_name,
        city: geoResponse.data.city,
        latitude: geoResponse.data.latitude,
        longitude: geoResponse.data.longitude
      };
    }
  } catch (error) {
    console.error('Error fetching IP or location data:', error);
    // Continue without IP/location data rather than failing
  }

  return userData;
};

export const sendAnalytics = async ({
  currentLanguage
}: SendAnalyticsParams): Promise<SendAnalyticsResponse> => {
  const apiUrl = process.env.REACT_APP_API_URL_PROMPT;
  const apiKey = process.env.REACT_APP_API_KEY;

  if (!apiUrl || !apiKey) {
    throw new Error('API URL or API Key is not defined');
  }

  try {
    // Collect all user data including IP and location in one step
    const userData = await collectUserData(currentLanguage);
    const authHeaders = await getAuthHeaders();

    const result = await axios.post(
      apiUrl,
      {
        prompt: JSON.stringify(userData),
        mode: 'hook',
        user: userData.userId
      },
      {
        headers: {
          'x-functions-key': apiKey,
          ...(authHeaders || {})
        }
      }
    );

    // Handle different response formats
    let content: string;
    if (Array.isArray(result.data.response)) {
      content = result.data.response[result.data.response.length - 1]?.content;
    } else if (typeof result.data.response === 'object' && result.data.response !== null) {
      content = result.data.response.content;
    } else if (typeof result.data.response === 'string') {
      content = result.data.response;
    } else {
      throw new Error('Invalid response format from API');
    }

    return { content };
  } catch (error) {
    console.error('Analytics error:', error);
    throw error;
  }
};